/**
 * Полоса горизонтальной прокрутки, закреплённая у нижнего края экрана.
 *
 * Зачем она нужна. Журнал заказов шире узкого окна, и блок с таблицей честно
 * прокручивается вбок. Но собственная полоса прокрутки живёт у НИЖНЕГО КРАЯ
 * ЭТОГО БЛОКА, а блок высотой в полторы сотни строк — то есть полоса
 * оказывается за десять тысяч точек вниз. Прокрутка есть, добраться до неё
 * нельзя: чтобы сдвинуть таблицу вбок, надо сначала долистать до последнего
 * заказа месяца.
 *
 * С 18.09.2026 в «Заказах» блок таблицы кончается у нижнего края окна и
 * строки листаются внутри него — там своя полоса на виду, и дублёр сам
 * убирается (см. ниже про «своя полоса видна»). В остальных разделах
 * прокручивается ОКНО, как записано в `Shell`, и дублёр по-прежнему нужен.
 *
 * Он прилипает к низу экрана, пока таблица видна, и связан с ней в обе
 * стороны: тянешь полосу — едет таблица, листаешь таблицу колесом с Shift —
 * едет полоса.
 *
 * Появляется только когда есть что прокручивать: если таблица помещается
 * целиком, полосы нет вовсе.
 */

import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Блок с горизонтальной прокруткой, за которым следим. */
  targetRef: React.RefObject<HTMLElement | null>;
}

export function StickyXScrollbar({ targetRef }: Props) {
  const selfRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [needed, setNeeded] = useState(false);

  /*
   * Ширина берётся у самого блока и в его же системе координат.
   *
   * `scrollWidth` и `scrollLeft` измеряются одинаково, поэтому значения можно
   * переносить один к одному, не пересчитывая. Это важно: у таблицы включён
   * масштаб (`zoom`), и любой самодельный пересчёт съезжал бы при 80% или 60%.
   */
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const измерить = () => {
      setWidth(target.scrollWidth);

      const естьЧтоПрокручивать = target.scrollWidth > target.clientWidth + 1;

      /*
       * Своя полоса таблицы ВИДНА — значит вторая не нужна.
       *
       * Дублёр существует ради длинных списков: там собственная полоса лежит
       * у нижнего края блока, за сотню строк вниз, и добраться до неё нельзя.
       * Но если заказов десяток, блок помещается в окно целиком, своя полоса
       * оказывается прямо на экране — и под ней рисовалась вторая, точно
       * такая же. Две одинаковые полосы подряд читаются как поломка: непонятно,
       * которая из них рабочая и чем они отличаются.
       *
       * Запас в пару точек — на дробные величины при масштабе окна: без него
       * полоса моргала бы, появляясь и исчезая на границе.
       */
      const низ = target.getBoundingClientRect().bottom;
      const свояПолосаВидна = низ <= window.innerHeight + 2;

      setNeeded(естьЧтоПрокручивать && !свояПолосаВидна);
    };

    измерить();

    // Ширина меняется от перетаскивания колонок, смены масштаба и размера окна.
    const observer = new ResizeObserver(измерить);
    observer.observe(target);
    const first = target.firstElementChild;
    if (first) observer.observe(first);
    window.addEventListener('resize', измерить);
    /*
     * Прокрутка ОКНА тоже меняет ответ: длинный список листают вниз, и когда
     * его конец вместе со своей полосой выходит на экран, дублёр должен уйти.
     */
    window.addEventListener('scroll', измерить, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', измерить);
      window.removeEventListener('scroll', измерить);
    };
  }, [targetRef]);

  /*
   * Связь в обе стороны, с защитой от кольца.
   *
   * Без флага «сейчас двигаю сам» каждый сдвиг одной полосы вызывал бы сдвиг
   * второй, та — снова первой, и прокрутка дёргалась бы под пальцем.
   */
  useEffect(() => {
    const target = targetRef.current;
    const self = selfRef.current;
    if (!target || !self) return;

    let свой = false;

    const отТаблицы = () => {
      if (свой) return;
      свой = true;
      self.scrollLeft = target.scrollLeft;
      свой = false;
    };
    const отПолосы = () => {
      if (свой) return;
      свой = true;
      target.scrollLeft = self.scrollLeft;
      свой = false;
    };

    target.addEventListener('scroll', отТаблицы, { passive: true });
    self.addEventListener('scroll', отПолосы, { passive: true });
    return () => {
      target.removeEventListener('scroll', отТаблицы);
      self.removeEventListener('scroll', отПолосы);
    };
  }, [targetRef, needed]);

  if (!needed) return null;

  return (
    <div
      ref={selfRef}
      /*
        `sticky bottom-0` держит полосу у нижнего края экрана, пока виден
        блок-родитель. Когда таблица прокручена до конца и уходит вверх,
        полоса уходит вместе с ней — это верно: прокручивать больше нечего.
      */
      className="sticky bottom-0 z-20 overflow-x-auto overflow-y-hidden"
      style={{
        // Высота под полосу браузера. Фон непрозрачный: под полосой проезжают
        // строки таблицы, и на просвет она читалась бы как часть данных.
        height: 14,
        background: 'var(--otc-bg)',
        borderTop: '1px solid var(--otc-line)',
      }}
      aria-hidden="true"
    >
      <div style={{ width, height: 1 }} />
    </div>
  );
}
